import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';

import TooltipWrapper from '@shared/TooltipWrapper';
import { ReactComponent as MoreIcon } from '@src/assets/escher/icons/expand-less.svg';
import { ReactComponent as LessIcon } from '@src/assets/escher/icons/expand-more.svg';

import HitDescription from './HitDescription';
import HitDetails from './HitDetails';

const EscalatedBtn = styled.div`
  background-color: ${props => props.theme.invalidBg};
  border-radius: 50%;
  border: none;
  color: ${props => props.theme.enigmaBlack};
  display: inline-block;
  font-family: 'GT', sans-serif;
  font-size: ${props => props.theme.fontLg};
  font-weight: 400;
  height: 36px;
  line-height: 24px;
  margin-right: 15px;
  padding: 5px;
  text-align: center;
  width: 36px;
`;

const Mono = styled.div`
  font-family: 'GT Mono', monospace;
`;
const ClearedBtn = styled(EscalatedBtn)`
  background-color: ${props => props.theme.validBg};
  color: ${props => props.theme.enigmaBlack};
`;

const LineContainer = styled.div`
  padding: 24px;
  width: 100%;
  display: inline-block;
  flex-wrap: wrap;
  align-items: center;
  span {
    margin-right: 5px;
  }
`;

const EntityId = styled.span`
  float: right;
`;
const DetailsContainer = styled.div`
  width: 100%;
  display: inline-block;
  padding: 0px 40px 40px 40px;
`;

const ExpandIndicator = styled.span`
  float: right;
`;

const RootContainer = styled.div`
  border-top: ${props => props.theme.borderMd};
  width: 100%;
  margin: 0 auto;
  display: inline-block;
  align-items: center;
`;

function AlertHit({
  alertId,
  id,
  index,
  entity_id,
  sanctioned_entity_details,
  list_metadata,
  explanation,
  recall_explanation,
  decision,
  hit_field,
}) {
  const [open, setOpen] = useState(false);

  const onClickWhileOpen = () => {
    setOpen(false);
  };

  const onClickWhileClosed = async () => {
    setOpen(true);
  };
  const handleClickOpen = open ? onClickWhileOpen : onClickWhileClosed;

  const alertHeader = (
    <LineContainer>
      {decision === 0 ? (
        <ClearedBtn>{index + 1}</ClearedBtn>
      ) : (
        <EscalatedBtn>{index + 1}</EscalatedBtn>
      )}
      <HitDescription explanation={explanation} hit_field={hit_field} listMetadata={list_metadata} />
      <ExpandIndicator>{open ? <MoreIcon /> : <LessIcon />}</ExpandIndicator>
      <EntityId>
        <TooltipWrapper placement='bottom' tooltip='Sanction Entity ID#'>
          <Mono>{entity_id}</Mono>
        </TooltipWrapper>
      </EntityId>
    </LineContainer>
  );

  if (!open) {
    return (
      <RootContainer>
        <div onClick={handleClickOpen}>{alertHeader}</div>
      </RootContainer>
    );
  }

  const MODEL_TYPE_LABELS = {
    FREETEXT: 'Using the Free Text model type',
    NAME: 'Using the Free Text model type',
    text: 'Using the Free Text model type',
  };

  return (
    <RootContainer>
      <div onClick={handleClickOpen}>{alertHeader}</div>
      <DetailsContainer>
        <div>{MODEL_TYPE_LABELS[explanation.model.model_type]}</div>
        <HitDetails
          alertId={alertId}
          hitId={id}
          decision={decision}
          explanation={explanation}
          recall_explanation={recall_explanation}
          sanctionedEntityDetails={sanctioned_entity_details}
          listMetadata={list_metadata}
        />
      </DetailsContainer>
    </RootContainer>
  );
}

AlertHit.propTypes = {
  id: PropTypes.number.isRequired, // hit ID
  alert_id: PropTypes.string.isRequired,
  hit_field: PropTypes.string.isRequired,
  entity_id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  decision: PropTypes.number.isRequired,
  sanctioned_entity_details: PropTypes.shape({
    names: PropTypes.array,
    sanctions_list: PropTypes.array,

  }).isRequired,
  explanation: PropTypes.shape({
    override: PropTypes.shape({
      decision: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
    }),
    model: PropTypes.shape({
      decision: PropTypes.number.isRequired,
      score: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
    }),
    threshold: PropTypes.shape({
      type: PropTypes.string.isRequired,
      decision: PropTypes.number.isRequired,
      minimum_score: PropTypes.number.isRequired,
      score: PropTypes.number.isRequired,
    }),
  }).isRequired,
};

AlertHit.displayName = 'AlertHit';

export default AlertHit;
